import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useCampaign, useDraft } from "./context";
import { AdventurePlay } from "./EntryPlay";
import { CampaignError, fieldText, SecretBadge, useAction, useEntries } from "./parts";
import { fieldOf, useFieldLabel } from "./play";
import type { CampaignEntryListItem } from "./types";

const order: Record<string, number> = { running: 0, planned: 1, completed: 2 };

/** The adventures with their status: begin, set back to planned, complete with summary and reward. */
export function AdventuresPage() {
  const campaign = useCampaign();
  const adventures = useEntries({ kind: "adventure" });
  if (!campaign.canWrite) return <p className="muted">Diese Seite gibt es in dieser Ansicht nicht.</p>;
  const sorted = [...(adventures.data ?? [])].sort(
    (a, b) => (order[String(fieldOf(a, "status") ?? "planned")] ?? 1) - (order[String(fieldOf(b, "status") ?? "planned")] ?? 1),
  );
  return (
    <section className="stack">
      <h2>{campaign.term("adventure")}</h2>
      <NewAdventure />
      {adventures.error && <CampaignError error={adventures.error} reload />}
      {adventures.isLoading && <p className="muted">Lade …</p>}
      {adventures.data && sorted.length === 0 && <p className="muted">Noch keine.</p>}
      <ul className="plain-list stack" aria-label={campaign.term("adventure")}>
        {sorted.map((a) => (
          <AdventureRow key={a.id} adventure={a} />
        ))}
      </ul>
    </section>
  );
}

function AdventureRow({ adventure }: { adventure: CampaignEntryListItem }) {
  const campaign = useCampaign();
  const goal = fieldOf(adventure, "goal");
  return (
    <li className="card stack" aria-label={adventure.title}>
      <div className="row">
        <h3 className="grow">
          <Link to={campaign.entryPath(adventure.id)}>{adventure.title}</Link>
        </h3>
        <SecretBadge visibility={adventure.visibility} />
      </div>
      {goal != null && <p className="muted small">{fieldText(goal)}</p>}
      <AdventurePlay entry={adventure} />
    </li>
  );
}

function NewAdventure() {
  const campaign = useCampaign();
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const action = useAction();
  const label = useFieldLabel();
  useDraft(title.trim() !== "" || goal.trim() !== "");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void action
      .run(async () => {
        if (!title.trim()) throw new Error("Der Eintrag braucht einen Titel.");
        const fields: Record<string, unknown> = { status: "planned" };
        if (goal.trim()) fields.goal = goal.trim();
        await campaign.apply({ type: "campaign.entry.create", payload: { kind: "adventure", title: title.trim(), fields } });
      })
      .then((ok) => {
        if (ok) {
          setTitle("");
          setGoal("");
        }
      });
  };
  return (
    <form className="card inline-form two" onSubmit={submit} aria-label={`Neu: ${campaign.term("adventure")}`}>
      <label className="field">
        <span>Titel</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="field">
        <span>{label("adventure", "goal")}</span>
        <input value={goal} onChange={(e) => setGoal(e.target.value)} />
      </label>
      <button type="submit" className="button secondary" disabled={action.busy}>
        Anlegen
      </button>
      <p className="muted small">{campaign.mode === "dossier" ? "Im Dossier angelegt ist der Eintrag geheim." : "Der Eintrag ist bekannt."}</p>
      <CampaignError error={action.error} onClose={action.clear} />
    </form>
  );
}
